import { NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { contractLabel, contractMonths, fmt } from './domain';
import { PC, UMR } from './tokens';
import type { ContractPeriod, DegreeDays, ProjectKind } from './types';

export type CopyStatus = 'idle' | 'copying' | 'done' | 'error';

interface Props {
  name: string;
  description: string;
  kind: ProjectKind;
  networkNames: string[];
  contract: ContractPeriod;
  perimeterLabel: string;
  buildingCount: number;
  incompleteRatios: number;
  stationName: string;
  dju: number;
  djclim: number;
  referenceDegrees: DegreeDays;
  referenceZone: string | undefined;
  projectZone: string | null;
  status: CopyStatus;
  progress: number;
  actions: React.ReactNode;
}

const COPY_STEPS = ['Copie des bâtiments UrbanMap', 'Application des ratios du projet', 'Correction climatique (DJU)', 'Création du projet'];

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: `1px solid ${UMR.borderSubtle}` }}>
      <span style={{ color: UMR.textSecondary }}>{label}</span>
      <strong style={{ textAlign: 'right' }}>{value}</strong>
    </div>
  );
}

/** Récapitulatif avant copie, puis progression de la copie (en cours / terminé). */
export function StepRecap(p: Props) {
  const copying = p.status !== 'idle';
  return (
    <div className="pcv3-recap-scroll">
      <div className="pcv3-recap-content">
        <NJText as="strong" scale="lg">Récapitulatif</NJText>
        <div className="pcv3-recap-columns">
        <section aria-label="Informations du projet">
          <NJText as="strong">Projet et périmètre</NJText>
          <Row label="Nom du projet" value={p.name} />
          <Row label="Description" value={p.description.trim() || 'Non renseignée'} />
          <Row label="Type de projet" value={p.kind === 'new' ? 'Nouveau réseau' : 'Réseau(x) existant(s)'} />
          {p.networkNames.length > 0 && <section className="pcv3-recap-networks" aria-label="Réseaux existants sélectionnés">
            <NJText as="strong" scale="sm">Réseaux existants sélectionnés ({p.networkNames.length})</NJText>
            <ul>
              {p.networkNames.map((name, index) => <li key={`${index}-${name}`}><NJText as="span" scale="sm">{name}</NJText></li>)}
            </ul>
          </section>}
          <Row label="Période du contrat" value={`${contractLabel(p.contract)} · ${contractMonths(p.contract)} mois`} />
          <Row label="Périmètre" value={p.perimeterLabel} />
          <Row label="Nombre de bâtiments copiés depuis UrbanMap" value={fmt(p.buildingCount)} />
          {p.incompleteRatios > 0 && <NJText role="alert">Calcul incomplet : {p.incompleteRatios} valeurs de ratios manquantes.
            Le projet les conserve sans les remplacer ; les consommations concernées restent indisponibles.</NJText>}
        </section>
        <div className="pcv3-recap-weather">
          <section aria-label="Référence des ratios">
            <NJText as="strong" className="pcv3-recap-climate-title">Référence des ratios</NJText>
            <Row label="Zone de référence des ratios" value={p.referenceZone || 'Non renseignée'} />
            <Row label="DJU de référence · chauffage" value={fmt(p.referenceDegrees.dju)} />
            <Row label="DJClim de référence · froid" value={fmt(p.referenceDegrees.djclim)} />
          </section>
          <section aria-label="Climat du projet">
            <NJText as="strong" className="pcv3-recap-climate-title">Climat du projet</NJText>
            <Row label="Zone climatique du périmètre" value={p.projectZone || 'Indéterminée'} />
            <Row label="Station météo retenue" value={p.stationName} />
            <Row label="DJU retenus · chauffage" value={fmt(p.dju)} />
            <Row label="DJClim retenus · froid" value={fmt(p.djclim)} />
          </section>
        </div>
        </div>
          <section className="pcv3-recap-notes" aria-label="À savoir avant de créer le projet">
            <div className="pcv3-recap-note">
              <NJText as="strong" scale="sm">Copie des données</NJText>
              <NJText scale="sm">Les bâtiments et leurs données seront figés à la date de création. Les mises à jour depuis UrbanMap se feront manuellement depuis le projet.</NJText>
            </div>
            <div className="pcv3-recap-note">
              <NJText as="strong" scale="sm">Consommations simulées</NJText>
              <NJText scale="sm">Les consommations seront estimées à partir de vos degrés-jours, ratios et coefficients. Ce ne sont ni des consommations mesurées ni des relevés météo.</NJText>
              <NJText scale="sm">Dans ce prototype, les degrés-jours proposés pour la station sont des valeurs de démonstration, modifiables avant le calcul.</NJText>
            </div>
            <div className="pcv3-recap-note">
              <NJText as="strong" scale="sm">Disponibilité du projet</NJText>
              <NJText scale="sm">Vous pourrez ouvrir le projet une fois sa création terminée.</NJText>
            </div>
          </section>
        {copying && (
          <section aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>{p.status === 'done' ? 'Projet créé' : p.status === 'error' ? 'La création a échoué' : 'Copie en cours…'}</span>
              <span>{Math.round(p.progress)} %</span>
            </div>
            <div style={{ height: 6, background: UMR.grey100 }}>
              <div style={{ height: '100%', width: `${p.progress}%`, background: p.status === 'error' ? PC.dangerText : PC.accent, transition: 'width 200ms' }} />
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {COPY_STEPS.map((s, i) => {
                const done = p.progress >= ((i + 1) / COPY_STEPS.length) * 100;
                return (
                  <li key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, color: done ? UMR.black : UMR.textSecondary }}>
                    <UMIcon name={done ? 'check_circle' : 'radio_button_unchecked'} size={18} color={done ? PC.accent : UMR.textSecondary} />
                    {i === 0 ? `${s} (${fmt(p.buildingCount)})` : s}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {p.actions}
      </div>
    </div>
  );
}
